import { Component, Inject, OnDestroy, OnInit } from "@angular/core"
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog"
import { CommunicationApiService } from "@api/communication/communication-api.service"
import { DropDownFieldsResponse, DropDownFieldValue, DynamicVariableDocumentation, DynamicVariableResponse } from "@api/communication/models/communication"
import { Arithmetic, NodeFunctionBody } from "@api/communication/models/functions"
import { FlowCardBadge } from "@shared/components/flow-card-v2/flow-card-v2.component"
import { DropDownDivider, DropDownFooter, DropDownItem } from "@shared/components/flow-drop-down/flow-drop-down.component"
import { FlowSegment } from "@shared/components/flow-segment-control/flow-segment-control.component"
import { LabelValue } from "@shared/models/LabelValue"
import { IconImageService } from "@shared/services/icon-image.service"
import { StateService } from "@shared/services/state.service"
import { ComparisonList, FilterableValues, ReservationStatuses } from "app/communication/automation/edit-flowchart/flow-node-dialog/flow-node-types"
import { FlowNodeService } from "app/communication/automation/flow-node.service"
import { ConditionBodyCodecService } from "./ag-conditional-dialog-codec.service"
import { HDComponent } from "app/hotel-directory/hotel-directory-edit/hotel-directory-edit-list"
import { finalize, forkJoin, Subject, takeUntil } from "rxjs"

type User = {
  FirstName: string
  LastName: string
}
export type filterFunctions = {
  infofilter: LabelValue
  comparer?: LabelValue
  reservationStatuses?: LabelValue[]
  offSetDays?: number
  usedFunction?: LabelValue
  svList?: string[]
  dateStart?: string
  dateEnd?: string
  selectedWeekDays?: string[]
  timeSet?: { from: string; to: string }
  svValues?: string[]
  visibilityDays?: TimeSpanConfig
  // set only when an ordinal status comparison was read back on load, holding the original
  // wording so the card can show what the condition used to say
  legacyStatusComparer?: string
  // set instead of a clean expansion when the old comparison has no equivalent selection
  legacyStatusIssue?: LegacyStatusIssue
}

export type WeekDayName = "Monday" | "Tuesday" | "Wednesday" | "Thursday" | "Friday" | "Saturday" | "Sunday"

export type DayTimeSpan = { from: string; to: string; AllDay: boolean }

export type TimeSpanConfig = {
  AllDay: boolean
  timeConfig: LabelValue
} & Partial<Record<WeekDayName, DayTimeSpan>>

export type VisibilityMode = "Show" | "Hide"
export type MatchMode = "all" | "any"
export type ConditionGroup = { title: string; conditions: LabelValue[] }
export type ConditionSetKey = "main" | "exception"

export type ConditionSet = {
  key: ConditionSetKey
  filterList: filterFunctions[]
  rulesList: LabelValue[][]
  collapsedGroups: Set<string>
  matchMode: MatchMode
}

const TIME_CONDITION_VALUES: string[] = ["StartDateTime", "EndDateTime", "WeekTimeSpan", "StartDate", "EndDate"]

// the backend's PMS wording differs from what hotels expect to see, so display names are
// translated for display only — `label` stays the backend value because the save path
// matches it back against the backend's displayName (see dynamicVariablePath)
const CONDITION_LABEL_TRANSLATIONS: Record<string, string> = {
  Space: "Room",
  "Space category": "Room category",
}

// these come from the backend's ReservationData, where `value` is only the C# type —
// so they can be told apart from the other reservation fields by display name alone
const ACCOMMODATION_CONDITION_LABELS: string[] = ["Space", "Space category"]

const NEGATION_FUNCTION_TYPE = "not"

// matches the offset slider's min/max in the template; each day shifts the readout one step
const OFFSET_LIMIT = 10
const OFFSET_STEP_PX = 35

const RESERVATION_STATUS_VALUE = "Reservation:Status"
const NUMBER_OF_NIGHTS = "NumberofNights"

// the only comparisons offered on a text condition, keyed on the backend rule's display
// name (lowercased). Acts as an allowlist — the backend also returns the negated variants
// ("list does not contain", "list match none"), which are deliberately not surfaced.
// Add an entry here to offer another comparison; order sets the segment order.
const MATCH_TYPE_COPY: Record<string, { label: string; description: string }> = {
  "list partial match any": { label: "Contains", description: "Matches if the {field} field contains any word or phrase below." },
  "list match any": { label: "Exact match", description: "Matches if the {field} field exactly matches any word or phrase below." },
}

// preferred default rule for a freshly added text condition, most specific first
const PREFERRED_MATCH_RULES: string[] = ["list partial match any", "list match any"]

// comparisons that older conditions were saved with but MATCH_TYPE_COPY no longer offers.
// The backend still evaluates them, so they are detected rather than silently rewritten -
// the user has to pick a supported match type before the dialog will save again.
const LEGACY_MATCH_RULE_NAMES: string[] = ["list does not contain", "list match none", "does not contain", "not in list", "list partial match none"]

export function isLegacyMatchRuleName(displayName: string | undefined): boolean {
  const name = displayName?.toLowerCase().trim() ?? ""
  if (!name) return false
  return !MATCH_TYPE_COPY[name] && LEGACY_MATCH_RULE_NAMES.includes(name)
}

// the status card has no comparer control — every status condition is an equality check
const DEFAULT_STATUS_COMPARER: LabelValue = { label: "is equal to", value: "==" }

const RESERVATION_STATUS_GROUPS: { title: string; values: string[] }[] = [
  { title: "Before stay", values: ["Created", "PreCheckedin"] },
  { title: "During stay", values: ["Checkedin"] },
  { title: "After stay", values: ["Checkedout"] },
  { title: "Other", values: ["Canceled", "NoShow"] },
]

// the stay lifecycle in order, which is what an ordinal status comparer ("<", ">") was
// ranking against. Canceled and NoShow are deliberately absent - they are not stages of a
// stay, so they were never meaningfully "before" or "after" another status
const RESERVATION_STATUS_LIFECYCLE: string[] = ["Created", "PreCheckedin", "Checkedin", "Checkedout"]

// comparers a status condition could once be saved with; the card now only offers equality,
// so these are expanded into the explicit list of statuses they used to cover
const ORDINAL_STATUS_COMPARERS: string[] = ["<", "<=", ">", "=>", ">="]

function isOrdinalStatusComparer(comparer: string | undefined): boolean {
  return !!comparer && ORDINAL_STATUS_COMPARERS.includes(comparer.trim())
}

// every lifecycle status the old ordinal comparison would have matched
function statusesMatchingOrdinal(comparer: string, boundary: string): string[] {
  const pivot = RESERVATION_STATUS_LIFECYCLE.indexOf(boundary)
  if (pivot === -1) return []

  return RESERVATION_STATUS_LIFECYCLE.filter((_, position) => {
    switch (comparer.trim()) {
      case "<":
        return position < pivot
      case "<=":
        return position <= pivot
      case ">":
        return position > pivot
      case ">=":
      case "=>":
        return position >= pivot
      default:
        return false
    }
  })
}

// why an ordinal status comparison could not simply be expanded into a selection:
//  - "offLifecycle": compared against Cancelled/No show, which are not stay stages, so the
//    old ordering had no meaning the picker can express
//  - "emptyRange": nothing precedes Created / follows Checked out, so it matched no status
type LegacyStatusIssue = "offLifecycle" | "emptyRange"

function ordinalStatusIssue(comparer: string, boundary: string): LegacyStatusIssue | null {
  if (!RESERVATION_STATUS_LIFECYCLE.includes(boundary)) return "offLifecycle"
  if (!statusesMatchingOrdinal(comparer, boundary).length) return "emptyRange"
  return null
}

function buildReservationStatusDividers(): DropDownDivider[] {
  let index = 0
  return RESERVATION_STATUS_GROUPS.map((group) => {
    const divider = { ...new DropDownDivider(), title: group.title, index }
    index += group.values.length
    return divider
  })
}

type IconKey = keyof IconImageService["Icon"]

// where a date-time bound landed in the parts list, so a start/end pair can be merged
type DateTimeBound = { value: string; index: number }

const CONDITION_ICON_KEYS: Record<string, IconKey> = {
  StartDateTime: "play",
  EndDateTime: "pause",
  WeekTimeSpan: "calendar",
  StartDate: "play",
  EndDate: "pause",
  [RESERVATION_STATUS_VALUE]: "table_reservation",
}

const CONDITION_ICON_FIELDS: Record<string, IconKey> = {
  NumberofNights: "moon",
  NumberOfChildren: "child",
  NumberOfAdults: "person",
  Comments: "message_empty",
  OTA: "hotel",
  PackageCodes: "tag",
  RatePlan: "suitcase",
  RatePlanCode: "suitcase",
  RoomNumber: "penthouse",
  RoomType: "double_bed",
}

@Component({
  selector: "app-ag-conditional-dialog",
  templateUrl: "./ag-conditional-dialog.component.html",
  styleUrl: "./ag-conditional-dialog.component.scss",
  standalone: false,
})
export class AgConditionalDialogComponent implements OnInit, OnDestroy {
  constructor(
    public dialogRef: MatDialogRef<AgConditionalDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public iconService: IconImageService,
    private flowNodeService: FlowNodeService,
    private communicationService: CommunicationApiService,
    private stateService: StateService,
    private codec: ConditionBodyCodecService,
  ) {}

  private unsubscribe = new Subject()
  public isLoading: boolean = false
  public readonly mainSet: ConditionSet = { key: "main", filterList: [], rulesList: [], collapsedGroups: new Set<string>(), matchMode: "all" }
  public readonly exceptionSet: ConditionSet = { key: "exception", filterList: [], rulesList: [], collapsedGroups: new Set<string>(), matchMode: "all" }
  public exceptionsEnabled: boolean = false
  public listenableEvents: LabelValue[] = [...FilterableValues]
  public reservationStatuses: LabelValue[] = [...ReservationStatuses]
  private functionList: Arithmetic[] = []
  public weekDays: string[] = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
  public readonly orderedWeekDays: string[] = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

  public readonly weekDayLabels: Record<string, WeekDayName> = {
    Mon: "Monday",
    Tue: "Tuesday",
    Wed: "Wednesday",
    Thu: "Thursday",
    Fri: "Friday",
    Sat: "Saturday",
    Sun: "Sunday",
  }

  public dropDownSupportList: DropDownFieldsResponse = {}
  public dynamicVariableList: DynamicVariableResponse[]
  private ReservationData: DynamicVariableResponse
  private dynamicDataFunction: DynamicVariableResponse
  private pmsVariables: DynamicVariableResponse
  private dvDocumentation: DynamicVariableDocumentation[] = []
  private functionOutput: NodeFunctionBody
  private editElement: HDComponent
  private hdId: string
  public context: string = ""
  public timeConfigurationOptions: LabelValue[] = [
    { label: "Same hours for all selected days", value: "SameHoursSelectedDays" },
    { label: "Different hours per day", value: "DifferentHoursSelectedDays" },
  ]

  public visibilityMode: VisibilityMode = "Show"
  // set when the caller passed a stored mode, so the reload does not have to guess it
  private visibilityModeKnown = false
  private activeSet: ConditionSet = this.mainSet

  public get filterList(): filterFunctions[] {
    return this.activeSet.filterList
  }

  public get rulesList(): LabelValue[][] {
    return this.activeSet.rulesList
  }

  public set rulesList(value: LabelValue[][]) {
    this.activeSet.rulesList = value
  }

  public get matchMode(): MatchMode {
    return this.activeSet.matchMode
  }

  private withSet<T>(set: ConditionSet, action: () => T): T {
    const previous = this.activeSet
    this.activeSet = set
    try {
      return action()
    } finally {
      this.activeSet = previous
    }
  }

  private readonly statusItemsCache = new WeakMap<filterFunctions, DropDownItem[]>()
  private readonly dropdownValueItemsCache = new WeakMap<filterFunctions, DropDownItem[]>()
  private readonly tagDrafts = new WeakMap<filterFunctions, string>()
  private readonly customTags = new WeakMap<filterFunctions, Set<string>>()
  // keyed by field, not by filter: deleting a custom value removes it for the whole hotel,
  // so it must survive the filter object being replaced and apply across both condition sets
  private readonly deletedCustomValues = new Map<string, Set<string>>()
  public readonly reservationStatusDividers: DropDownDivider[] = buildReservationStatusDividers()
  public readonly reservationStatusFooter: DropDownFooter = { tertiaryText: "", secondaryText: "Cancel", primaryText: "Select" }
  public readonly dropdownValueFooter: DropDownFooter = { tertiaryText: "", secondaryText: "Cancel", primaryText: "Select" }
  public readonly visibilitySegments: FlowSegment[] = [
    { label: "Show", value: "Show" },
    { label: "Hide", value: "Hide" },
  ]

  public readonly matchSegments: FlowSegment[] = [
    { label: "all", value: "all" },
    { label: "any", value: "any" },
  ]

  // ===== Lifecycle =====
  ngOnInit(): void {
    this.editElement = this.data.element
    this.hdId = this.data.Id
    this.context = this.data.context
    if (this.data.visibilityMode === "Show" || this.data.visibilityMode === "Hide") {
      this.visibilityMode = this.data.visibilityMode
      this.visibilityModeKnown = true
    }
    // the dialog is unusable until the catalogue and any saved condition are in, so one
    // spinner covers both stages; each exit path clears it
    this.isLoading = true
    forkJoin([
      this.communicationService.Function.GetArithmetic(),
      this.communicationService.Communication.GetDynamicVariableList(this.stateService.selectedHotel.Id, false),
      this.communicationService.Communication.GetDynamicVariableListDocumentation(this.stateService.selectedHotel.Id),
      this.communicationService.Communication.GetDropDownFieldsAll(this.stateService.selectedHotel.Id),
    ])
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        error: () => (this.isLoading = false),
        next: ([functionList, dynamicVariableList, DVDocumentation, dropDownFields]) => {
          this.functionList = functionList
          this.dropDownSupportList = dropDownFields
          this.codec.use({ functionList })
          if (this.context === "HDSections") {
            this.listenableEvents = []
            this.listenableEvents.push({ label: "Start date & time", value: "StartDateTime" }) // value not in BE yet, only hotel directory -Simon 2026-03
            this.listenableEvents.push({ label: "End date & time", value: "EndDateTime" }) // value not in BE yet, only hotel directory -Simon 2026-03
            this.listenableEvents.push({ label: "Day of the week", value: "WeekTimeSpan" }) // value not in BE yet, only hotel directory -Simon 2026-03
            this.dvDocumentation = DVDocumentation.filter((x) => x.availableContexts.includes("HotelDirectory"))
            this.dvDocumentation.forEach((doc) => {
              const fieldName = doc.fieldName.replaceAll(".", ":")
              const value = fieldName === RESERVATION_STATUS_VALUE ? RESERVATION_STATUS_VALUE : doc.type
              this.listenableEvents.push({ label: doc.displayName, value, fieldName, dynamicVariable: true, dropdownSupport: doc.dropdownSupport ?? false })
            })
          } else {
            this.dynamicVariableList = dynamicVariableList
            this.addPmsVariablesToList()
            this.addReservationDataToList()
            this.addGenericDynamicDataToList()
          }

          if (!this.editElement.condition) {
            this.isLoading = false
            return
          }

          this.communicationService.Function.GetFunction(this.stateService.selectedHotel.Id, this.hdId, this.editElement.condition)
            .pipe(
              finalize(() => (this.isLoading = false)),
              takeUntil(this.unsubscribe),
            )
            .subscribe((func) => {
              var body = JSON.parse(func._body)
              this.reloadConditionBody(body)
              this.updateFilterOutput()
            })
        },
      })
  }

  ngOnDestroy(): void {
    this.unsubscribe.next(null)
    this.unsubscribe.complete()
  }

  // ===== Loading saved conditions =====
  private reloadConditionBody(body: any): void {
    // a "Hide if" condition is the whole body wrapped in a negate; unwrap it first so the
    // inner body reloads exactly like a "Show if" one. The mode is stored on the element,
    // so it is only inferred from the wrapper for records saved before that was persisted
    const inner = this.codec.unwrapNegation(body)
    if (inner) {
      if (!this.visibilityModeKnown) this.visibilityMode = "Hide"
      body = inner
    }

    const children: any[] = body?.ChildBodies ?? []
    const negatedIndex = children.findIndex((child) => child.BodyData === NEGATION_FUNCTION_TYPE)

    if (negatedIndex === -1) {
      this.reloadSetBody(body, this.mainSet)
      return
    }

    const mainChildren = children.filter((_, i) => i !== negatedIndex)
    const mainBody = mainChildren.length === 1 && this.isSetCombinator(mainChildren[0]) ? mainChildren[0] : { BodyData: "xAND", ChildBodies: mainChildren }
    this.reloadSetBody(mainBody, this.mainSet)

    const exceptionBody = children[negatedIndex].ChildBodies?.[0]
    if (!exceptionBody) return
    this.exceptionsEnabled = true
    this.reloadSetBody(exceptionBody, this.exceptionSet)
  }

  private isSetCombinator(element: any): boolean {
    if (element?.BodyData !== "xAND" && element?.BodyData !== "xOR") return false
    if (element.BodyData === "xAND") return true
    return element.ChildBodies?.some((child: any) => child.BodyData !== "withinTimeSpan") ?? false
  }

  private reloadSetBody(body: any, set: ConditionSet): void {
    set.matchMode = body?.BodyData === "xOR" ? "any" : "all"
    this.withSet(set, () => {
      const children: any[] = body?.ChildBodies ?? []
      children.forEach((element, index) => this.reloadFilterElement(element, index))
    })
  }

  private reloadFilterElement(element: any, index: number): void {
    if (element.BodyData === "statusCompare") {
      this.pushStatusFilter([element])
    } else if (element.BodyData === "dateCompare" || element.BodyData === "dateCompareAfter" || element.BodyData === "dateCompareBefore") {
      this.reloadDateCompareFunction(element)
    } else if (element.BodyData === "dateBetween") {
      this.reloadDateBetweenFunction(element)
    } else if (element.BodyData === "xOR") {
      this.reloadXORFunction(element, index)
    } else if (element.BodyData === "xAND") {
      this.reloadNumericRange(element, index)
    } else {
      this.reloadDVFunctions(element, index)
      this.reslotNumericBound(element, index)
    }
  }

  private pushStatusFilter(elements: any[]): void {
    const statuses = elements.map((element) => ReservationStatuses.find((x) => x.value === element.ChildBodies[1].BodyData)).filter((status): status is LabelValue => !!status)
    if (!statuses.length) return

    const comparerValue = elements[0].ChildBodies[2].BodyData
    const legacy = this.readOrdinalStatuses(comparerValue, statuses)

    this.filterList.push({
      comparer: legacy ? { ...DEFAULT_STATUS_COMPARER } : { label: ComparisonList.find((x) => x.value === comparerValue)?.label ?? DEFAULT_STATUS_COMPARER.label, value: comparerValue },
      infofilter: { label: "Reservation Status", value: RESERVATION_STATUS_VALUE, fieldName: RESERVATION_STATUS_VALUE },
      reservationStatuses: legacy ? legacy.statuses : statuses,
      legacyStatusComparer: legacy?.description,
      legacyStatusIssue: legacy?.issue,
    })
  }

  // "Status is less than Checked in" has no equivalent control any more. Where the old
  // comparison maps onto the stay lifecycle it becomes that set of statuses; where it does
  // not, the original selection is kept and the issue recorded so the card can say why the
  // condition needs a decision. Returns null for an equality check, which needs nothing.
  private readOrdinalStatuses(comparerValue: string, statuses: LabelValue[]): { statuses: LabelValue[]; description: string; issue?: LegacyStatusIssue } | null {
    if (!isOrdinalStatusComparer(comparerValue)) return null
    if (statuses.length !== 1) return null

    const boundary = statuses[0]
    const comparerLabel = ComparisonList.find((x) => x.value === comparerValue)?.label ?? comparerValue
    const description = `Reservation Status ${comparerLabel} "${boundary.label}"`
    const issue = ordinalStatusIssue(comparerValue, boundary.value)

    if (issue) return { statuses, description, issue }

    const expandedValues = statusesMatchingOrdinal(comparerValue, boundary.value)
    return {
      statuses: ReservationStatuses.filter((status) => expandedValues.includes(status.value)),
      description,
    }
  }

  private reloadXORFunction(element: any, index: number): void {
    const children: any[] = element.ChildBodies ?? []
    if (children.length && children.every((x) => x.BodyData === "statusCompare")) {
      this.pushStatusFilter(children)
      return
    }
    if (children.findIndex((x) => x.BodyData !== "withinTimeSpan") !== -1) return
    this.filterList[index] = {
      infofilter: { label: "Day of the week", value: "WeekTimeSpan" },
    }
    var differentTime: LabelValue[] = []
    element.ChildBodies.forEach((element) => {
      if (element.BodyData === "withinTimeSpan") {
        var day = element.ChildBodies[1].BodyData
        var startTime = element.ChildBodies[2].BodyData.replaceAll('"', "").substring(0, 5)
        var endTime = element.ChildBodies[3].BodyData.replaceAll('"', "").substring(0, 5)
        if (!this.filterList[index].visibilityDays) {
          this.filterList[index].visibilityDays = this.initVisibilityDaysConfig()
        }
        var AllDayCheck = startTime === "00:00" && endTime === "23:59"
        this.filterList[index].visibilityDays[this.dayNameFromLabel(this.weekDays[day])] = { from: startTime, to: endTime, AllDay: AllDayCheck }
        this.filterList[index].selectedWeekDays ? this.filterList[index].selectedWeekDays.push(this.weekDays[day]) : (this.filterList[index].selectedWeekDays = [this.weekDays[day]])
        differentTime.push({ label: startTime, value: endTime })
        this.filterList[index].visibilityDays.AllDay = AllDayCheck
      }
    })
    var sameTimeForAllDays = differentTime.every((x) => x.label === differentTime[0].label && x.value === differentTime[0].value)
    this.filterList[index].timeSet = sameTimeForAllDays ? { from: differentTime[0].label, to: differentTime[0].value } : null
    this.filterList[index].visibilityDays.timeConfig = sameTimeForAllDays ? this.timeConfigurationOptions[0] : this.timeConfigurationOptions[1]
  }

  // a numeric range saves as an xAND of a ">=" and a "<=" comparison; the first child
  // rebuilds the condition card and each child's value lands back in its min/max slot
  private reloadNumericRange(element: any, index: number): void {
    const children: any[] = element.ChildBodies ?? []
    if (!children.length) return

    this.reloadDVFunctions(children[0], index)
    const filter = this.filterList[index]
    if (!filter || !this.isNumericFilter(filter)) return

    filter.svValues = []
    children.forEach((child) => {
      const value = child.ChildBodies?.[1]?.BodyData
      if (value === null || value === undefined) return
      filter.svValues[this.codec.numericSlotFor(child.BodyData)] = String(value)
    })
    // the max bound is optional, so the min slot must still be a string for the template
    filter.svValues[0] = filter.svValues[0] ?? ""
  }

  // a bound saved on its own is a plain comparison, not an xAND, so its value arrives
  // in slot 0; move it to the max slot when the rule is a "<=" so the template still
  // renders it under Max. and the min field stays empty
  private reslotNumericBound(element: any, index: number): void {
    const filter = this.filterList[index]
    if (!filter || !this.isNumericFilter(filter)) return

    const slot = this.codec.numericSlotFor(element.BodyData)
    if (slot === 0) return

    const value = filter.svValues?.[0] ?? ""
    filter.svValues = ["", String(value)]
  }

  private reloadDVFunctions(element: any, index: number): void {
    var usedFunction = this.functionList.find((x) => x.functionType === element.BodyData)
    if (!usedFunction || !element.ChildBodies?.length) return
    var dynamicVariable = element.ChildBodies[0].BodyData
    var normalized = dynamicVariable.replace(/^Reservation:Reservation[:.]/, "Reservation:")
    var parts = normalized.split(":")
    var lastPart = parts[parts.length - 1].split(".").pop().replaceAll(" ", "_")

    if (this.context === "HDSections") {
      const event = this.listenableEventFromDvName(normalized, lastPart)
      if (!event) return
      this.addChosenConditional(event, index)
      this.setRule({ label: usedFunction.displayName, value: usedFunction.parameters[1]?.type ?? null, functionType: usedFunction.functionType }, index)
      this.filterList[index].comparer = { label: usedFunction.displayName, value: usedFunction.parameters[1]?.type ?? null, functionType: usedFunction.functionType }
      this.pushSvValues(element, index)
      return
    }

    var dv = this.pmsVariables?.properties.find((x) => x.name === lastPart)
    var isPmsVariable = true
    var dvFieldName: string | undefined
    if (dv === undefined) {
      isPmsVariable = false
      if (lastPart === NUMBER_OF_NIGHTS) {
        dv = this.dynamicDataFunction?.properties.find((x) => x.name === lastPart)
      } else {
        const reservationProps = this.dynamicVariableList?.find((x) => x.typeName === "ReservationData")?.properties
        dv = reservationProps?.find((x) => x.name === lastPart || x.name === normalized || x.name.endsWith(":" + lastPart))
      }
    }

    var usedType = usedFunction.parameters[0].type
    if (usedFunction.parameters[0].type === "T") {
      usedType = dv?.type
    }
    this.addChosenConditional({ label: dv?.displayName, value: usedType, fieldName: dvFieldName, dynamicVariable: true, pmsVariable: isPmsVariable }, index)
    this.setRule({ label: usedFunction.displayName, value: usedFunction.parameters[1]?.type ?? null, functionType: usedFunction.functionType }, index)
    this.filterList[index].comparer = { label: usedFunction.displayName, value: usedFunction.parameters[1]?.type ?? null, functionType: usedFunction.functionType }
    this.pushSvValues(element, index)
  }

  private listenableEventFromDvName(normalized: string, lastPart: string): LabelValue | undefined {
    return this.listenableEvents.find((x) => x.dynamicVariable && (x.fieldName === normalized || x.fieldName === lastPart || x.fieldName?.endsWith(":" + lastPart)))
  }

  private pushSvValues(element: any, index: number): void {
    if (element.ChildBodies[1]?.BodyDataType === "String" || element.ChildBodies[1]?.BodyDataType === "Int32" || element.ChildBodies[1]?.BodyDataType === "Double" || element.ChildBodies[1]?.BodyDataType === "Decimal") {
      this.filterList[index].svValues.push(element.ChildBodies[1].BodyData)
    } else {
      if (element.ChildBodies[1]) {
        this.filterList[index].svValues = element.ChildBodies[1].BodyData
      }
    }
  }

  private reloadDateBetweenFunction(element: any): void {
    if (element.ChildBodies[1].BodyType === "sv") {
      var startDate = element.ChildBodies[1].BodyData
      var endDate = element.ChildBodies[2].BodyData
      this.filterList.push({
        infofilter: { label: "Date Range", value: "DateRange" },
        dateStart: startDate,
        dateEnd: endDate,
      })
    } else {
      var infoFilterValue = element.ChildBodies[1].BodyData.split(":")[1]
      var offSetDays = element.ChildBodies[2].ChildBodies[1].BodyData.split(".")[0]
      if (offSetDays === "00:00:00") {
        offSetDays = 0
      }
      var comparer = element.ChildBodies[2].BodyType
      var beforeAfter = this.flowNodeService.dateBetweenComparerFromOptions(offSetDays, comparer)
      this.filterList.push({
        infofilter: { label: FilterableValues.find((x) => x.value === infoFilterValue).label, value: element.ChildBodies[1].BodyData.split(":")[1] },
        comparer: { label: beforeAfter, value: beforeAfter },
        offSetDays: offSetDays,
      })
    }
  }

  private reloadDateCompareFunction(element: any): void {
    if (element.ChildBodies[1].BodyType === "sv") {
      var startCondition = element.BodyData === "dateCompareAfter" ? { label: "Start date & time", value: "StartDateTime" } : { label: "End date & time", value: "EndDateTime" }
      var startDate = element.ChildBodies[1].BodyData
      var comparer = element.ChildBodies[2].BodyData
      var time = element.BodyData === "dateCompareAfter" ? { from: startDate.split("T")[1].substring(0, 5), to: null } : { from: null, to: startDate.split("T")[1].substring(0, 5) }
      this.filterList.push({
        infofilter: { label: startCondition.label, value: startCondition.value },
        dateStart: element.BodyData === "dateCompareAfter" ? startDate.split("T")[0] : null,
        dateEnd: element.BodyData === "dateCompareAfter" ? null : startDate.split("T")[0],
        timeSet: time,
        comparer: { label: ComparisonList.find((x) => x.value === comparer).label, value: comparer },
      })
    } else {
      var infoFilterValue = element.ChildBodies[1].ChildBodies[0].BodyData.split(":")[1]
      var offSetDays = element.ChildBodies[1].ChildBodies[1].BodyData.split(".")[0]
      if (offSetDays === "00:00:00") {
        offSetDays = 0
      }
      var comparer = element.ChildBodies[2].BodyData
      var beforeAfter = this.flowNodeService.getBeforeOrAfterFromOptions(offSetDays, comparer)
      this.filterList.push({
        infofilter: { label: FilterableValues.find((x) => x.value === infoFilterValue).label, value: element.ChildBodies[1].ChildBodies[0].BodyData.split(":")[1] },
        comparer: { label: beforeAfter, value: beforeAfter },
        offSetDays: offSetDays,
      })
    }
  }

  private addPmsVariablesToList(): void {
    if (this.dynamicVariableList.find((x) => x.typeName === "PmsVariables")) {
      this.pmsVariables = this.dynamicVariableList.find((x) => x.typeName === "PmsVariables")
      this.pmsVariables.properties.forEach((property) => {
        if (this.listenableEvents.findIndex((x) => x.label === property.displayName) === -1) {
          this.listenableEvents.push({ label: property.name.replaceAll("_", " "), value: property.type, dynamicVariable: true, pmsVariable: true })
        }
      })
    }
  }

  private addReservationDataToList(): void {
    this.ReservationData = this.dynamicVariableList.find((x) => x.typeName === "ReservationData")
    this.ReservationData.properties.forEach((property) => {
      if (this.listenableEvents.findIndex((x) => x.label === property.displayName) === -1 && property.type !== "PmsVariables" && property.type !== "KeyVariables") {
        this.listenableEvents.push({ label: property.displayName, value: property?.type, dynamicVariable: true, pmsVariable: false })
      }
    })
  }

  private addGenericDynamicDataToList(): void {
    this.dynamicDataFunction = this.dynamicVariableList.find((x) => x.typeName === "DynamicDataFunction")
    this.dynamicDataFunction.properties.forEach((property) => {
      if (property.type === "Int32" && property.name === NUMBER_OF_NIGHTS && this.listenableEvents.findIndex((x) => x.label === property.displayName) === -1) {
        this.listenableEvents.push({ label: property.displayName, value: property?.type, dynamicVariable: true, pmsVariable: false })
      }
    })
  }

  // ===== Condition state =====
  addChosenConditional(event: any, index: number): void {
    if (event.fieldName === "Reservation.Status" || event.fieldName === RESERVATION_STATUS_VALUE) {
      event.value = RESERVATION_STATUS_VALUE
    }

    if (this.filterList.length === 0 && (event.label === "Arrival date" || event.label === "Departure date")) {
      this.filterList.push({
        infofilter: event,
        comparer: { label: "", value: "" },
        offSetDays: 0,
      })
    } else if (this.filterList.length === 0) {
      this.filterList.push({ infofilter: event })
    } else {
      if (this.filterList[index] === undefined) {
        this.filterList[index] = { infofilter: event }
      } else {
        const offSetDays = event.label === "Arrival date" || event.label === "Departure date" ? 0 : undefined
        this.filterList[index] = { infofilter: event, svList: [], svValues: [], offSetDays }
      }
    }
    if (event.label === "Day of the week") {
      const visibilityDays = this.filterList[index].visibilityDays ?? this.initVisibilityDaysConfig()
      visibilityDays.timeConfig = { label: "Same hours for all selected days", value: "SameHoursSelectedDays" }
      this.filterList[index] = { ...this.filterList[index], visibilityDays }
    }
    if (this.filterList[index].infofilter.dynamicVariable) {
      this.rulesList[index] = []
      this.filterList[index] = {
        ...this.filterList[index],
        comparer: undefined,
        usedFunction: undefined,
        svList: [],
        svValues: [],
      }
    }

    // seeded after the dynamicVariable reset above, which would otherwise clear the
    // comparer — the status card has no comparer control to set it back
    if (event.value === RESERVATION_STATUS_VALUE) {
      this.filterList[index] = {
        ...this.filterList[index],
        comparer: { ...DEFAULT_STATUS_COMPARER },
        reservationStatuses: this.filterList[index].reservationStatuses ?? [],
      }
    }

    //Fill pms variable rules
    this.functionList.forEach((item) => {
      var currentFilterType = this.filterList[index].infofilter.value
      var notAlreadyExists = this.rulesList[index]?.findIndex((x) => x.label === item.displayName) === -1 || this.rulesList[index] === undefined
      if (item.returnType !== "Boolean") return
      if (!item.useableInSegmentation) return
      if (item.parameters.length >= 3 && event.label !== "Day of the week") return
      if (currentFilterType === item.parameters[0].type || item.parameters[0].type === "T") {
        if (item.parameters[0].type === "T" && item.supportedGenericTypes?.includes(currentFilterType)) {
          notAlreadyExists ? this.rulesList[index].push({ label: item.displayName, value: item.parameters[1].type, functionType: item.functionType }) : ""
          return
        }
        if (notAlreadyExists && item.parameters[0].type !== "T") {
          this.rulesList[index].push({ label: item.displayName, value: item.parameters[1]?.type ?? null, functionType: item.functionType })
        }
      }
    })

    // a condition added from the UI has no rule picked yet, which would leave usedFunction
    // undefined and keep Save disabled — seed the preferred comparison so the card is usable
    if (this.filterList[index].infofilter.dynamicVariable && !this.filterList[index].usedFunction) {
      const rule = this.defaultMatchRule(index)
      if (rule) this.setRule(rule, index)
    }
  }

  setRule(event: any, index: number): void {
    var usedFunction = this.functionList.find((x) => x.functionType === event.functionType)
    if (!usedFunction) return
    this.filterList[index].usedFunction = event
    this.filterList[index].svList = []
    this.filterList[index].svValues = []
    usedFunction.parameters.slice(1).forEach((x) => {
      if (x.type === "T") {
        var dynamicVariableType = this.filterList[index].infofilter.value
        this.filterList[index].svList.push(dynamicVariableType)
        return
      } else {
        this.filterList[index].svList.push(x.type)
      }
    })
    this.updateFilterOutput()
  }

  private defaultMatchRule(index: number): LabelValue | undefined {
    const rules = this.rulesList[index] ?? []
    for (const preferred of PREFERRED_MATCH_RULES) {
      const match = rules.find((rule) => rule.label?.toLowerCase() === preferred)
      if (match) return match
    }
    // reached only by fields with no allowlisted comparison at all (numeric ranges, etc.),
    // which have no match-type control and so can safely take the backend's first rule
    return rules[0]
  }

  public selectDay(event: any, index: number): void {
    if (this.filterList[index].selectedWeekDays?.includes(event)) {
      this.filterList[index].selectedWeekDays.splice(this.filterList[index].selectedWeekDays.indexOf(event), 1)
      if (this.filterList[index].visibilityDays[this.dayNameFromLabel(event)]) {
        this.filterList[index].visibilityDays[this.dayNameFromLabel(event)] = { from: null, to: null, AllDay: false }
      }
    } else {
      if (!this.filterList[index].selectedWeekDays) {
        this.filterList[index].selectedWeekDays = []
      }
      this.filterList[index].selectedWeekDays.push(event)
      //Set default value for new day
      if (this.filterList[index].visibilityDays[this.dayNameFromLabel(event)].from === null || this.filterList[index].visibilityDays[this.dayNameFromLabel(event)].to === null) {
        this.filterList[index].visibilityDays[this.dayNameFromLabel(event)] = { from: "00:00", to: "23:59", AllDay: true }
      }
    }
    this.updateFilterOutput()
  }

  // touching the selection means the migrated statuses have been reviewed, so the notice
  // about the old comparison has served its purpose
  public setReservationStatus(statuses: LabelValue[], index: number): void {
    this.filterList[index].reservationStatuses = statuses
    this.filterList[index].legacyStatusComparer = undefined
    this.filterList[index].legacyStatusIssue = undefined
    this.filterList[index].comparer = { ...DEFAULT_STATUS_COMPARER }
    this.updateFilterOutput()
  }

  public setDate(event: Date[], index: number, type: "dateStart" | "dateEnd"): void {
    this.filterList[index][type] = event[0].toLocaleDateString("sv-SE")
    this.updateFilterOutput()
  }

  public changeOffset(event: any, index: number): void {
    const newOffset = parseInt(event.target.value)
    this.filterList[index].offSetDays = Number.isNaN(newOffset) ? 0 : newOffset
    this.updateFilterOutput()
  }

  public beforeAfterOffset(direction: string, index: number): void {
    if (direction === "Before") {
      this.filterList[index].comparer = { label: "BeforeOffset", value: "Before" }
    }
    if (direction === "After") {
      this.filterList[index].comparer = { label: "AfterOffset", value: "After" }
    }
    this.updateFilterOutput()
  }

  public allDayChange(event: any, index: number, specificDay: string): void {
    if (!this.filterList[index]?.visibilityDays) {
      this.filterList[index].visibilityDays = this.initVisibilityDaysConfig()
    }
    if (specificDay) {
      var dayFromLabel = this.dayNameFromLabel(specificDay)
      this.filterList[index].visibilityDays[dayFromLabel].AllDay = event
    } else {
      this.filterList[index].visibilityDays.AllDay = event
    }
    this.updateFilterOutput()
  }

  public timeConfigChange(option: LabelValue, index: number): void {
    if (!this.filterList[index].visibilityDays) {
      this.filterList[index].visibilityDays = this.initVisibilityDaysConfig()
    }
    this.filterList[index].visibilityDays.timeConfig = option
    this.updateFilterOutput()
  }

  public tagsInput(event: any, index: number) {
    this.filterList[index].svValues = event
    this.updateFilterOutput()
  }

  private rangeInput(event: any, index: number, slot: 0 | 1): void {
    const values = this.filterList[index].svValues ?? []
    // keep both slots as strings so a max-only range never leaves a hole at slot 0
    values[0] = values[0] ?? ""
    values[slot] = this.nonNegativeNumber(event)
    this.filterList[index].svValues = values
    this.updateFilterOutput()
  }

  private nonNegativeNumber(value: unknown): string {
    const text = String(value ?? "").trim()
    if (text === "-") return ""
    return text.startsWith("-") ? "0" : text
  }

  private initVisibilityDaysConfig(): TimeSpanConfig {
    const config: TimeSpanConfig = { AllDay: true, timeConfig: null }
    this.orderedWeekDays.forEach((day) => (config[this.dayNameFromLabel(day)] = { from: null, to: null, AllDay: true }))
    return config
  }

  public onTimeChange(event: Event, type: "from" | "to", index: number, day: string): void {
    const input = event.target as HTMLInputElement
    if (!input) return
    let value = input.value.replace(/[^\d:]/g, "")
    const digits = value.replace(/:/g, "")
    let formatted = ""
    if (digits.length === 3) {
      formatted = digits[0].padStart(2, "0") + ":" + digits.slice(1).padEnd(2, "0")
    } else if (digits.length === 4) {
      formatted = digits.slice(0, 2) + ":" + digits.slice(2)
    } else if (digits.length <= 2 && value.includes(":")) {
      formatted = digits.padStart(2, "0") + ":"
    } else if (digits.length <= 2 && digits.length > 0) {
      formatted = digits.padStart(2, "0") + ":00"
    } else {
      formatted = value
    }
    const isValid = /^([01]\d|2[0-3]):([0-5]\d)$/.test(formatted) //Validate only 00:00 to 23:59
    input.value = isValid ? formatted : null
    if (isValid) {
      if (day === null) {
        if (!this.filterList[index].timeSet) {
          this.filterList[index].timeSet = { from: null, to: null }
        }
        this.filterList[index].timeSet[type] = formatted
      } else if (day) {
        var dayFromLabel = this.dayNameFromLabel(day)
        this.filterList[index].visibilityDays[dayFromLabel][type] = formatted
      }
      this.updateFilterOutput()
    } else {
      this.filterList[index].timeSet = { from: null, to: null }
    }
  }

  public onTimeKeydown(event: KeyboardEvent): void {
    const allowed = /^[\d:]$/.test(event.key)
    const isControl = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(event.key)
    if (!allowed && !isControl) {
      event.preventDefault()
      return
    }
    const input = event.target as HTMLInputElement
    const hasSelection = input.selectionStart !== input.selectionEnd
    if (allowed && !hasSelection && input.value.replace(/[^\d]/g, "").length >= 4) event.preventDefault()
  }

  // ===== Serialising the condition body =====
  // the body itself is built by the codec; the dialog only supplies the two sets and the mode
  updateFilterOutput(): void {
    this.functionOutput = this.codec.build(this.mainSet, this.exceptionsEnabled ? this.exceptionSet : null, this.visibilityMode)
  }

  // ===== Validation =====
  private isFilterValid(filter: filterFunctions): boolean {
    if (!filter.infofilter?.value) return false

    switch (filter.infofilter.value) {
      case RESERVATION_STATUS_VALUE:
        // an old comparison that expanded cleanly is already a valid selection; one that
        // could not be expanded still holds its ordinal comparer, so it has to be redone
        if (filter.legacyStatusIssue) return false
        return !!filter.reservationStatuses?.length

      case "StartDate":
      case "EndDate":
        return !!filter.comparer?.value

      case "StartDateTime":
        return !!filter.dateStart && !!filter.timeSet?.from

      case "EndDateTime":
        return !!filter.dateEnd && !!filter.timeSet?.to

      case "WeekTimeSpan":
        return this.isWeekTimeSpanValid(filter)

      default:
        if (!filter.infofilter.dynamicVariable) return false
        if (!filter.usedFunction?.functionType) return false
        // an unsupported comparison has to be replaced before the condition can be saved,
        // otherwise the stale rule would be re-serialised behind a control that shows nothing
        if (this.legacyRuleLabelFor(filter)) return false
        if (this.isNumericFilter(filter)) return this.codec.numericBoundValue(filter, 0) !== null || this.codec.numericBoundValue(filter, 1) !== null
        if ((filter.svList?.length ?? 0) > 0 && (!filter.svValues?.length || filter.svValues.every((v) => !v))) return false
        return true
    }
  }

  private isWeekTimeSpanValid(filter: filterFunctions): boolean {
    if (!filter.selectedWeekDays?.length || !filter.visibilityDays?.timeConfig) return false
    if (filter.visibilityDays.timeConfig.value === "SameHoursSelectedDays" && !filter.visibilityDays.AllDay) {
      return !!filter.timeSet?.from && !!filter.timeSet?.to
    }
    return true
  }

  private numericConditionText(filter: filterFunctions, field: string): string {
    const min = this.codec.numericBoundValue(filter, 0)
    const max = this.codec.numericBoundValue(filter, 1)
    if (min !== null && max !== null) return `${field} is between ${min} and ${max}`
    if (max !== null) return `${field} is ${max} or less`
    return `${field} is ${min ?? ""} or more`.trim()
  }

  private isNumericFilter(filter: filterFunctions): boolean {
    return this.codec.isNumericFilter(filter)
  }

  public disableConfirmButton(): boolean {
    if (this.mainSet.filterList.length === 0) return true
    if (this.mainSet.filterList.some((filter) => !this.isFilterValid(filter))) return true
    if (this.exceptionsEnabled && this.exceptionSet.filterList.some((filter) => !this.isFilterValid(filter))) return true
    return this.conditionWarnings().length > 0
  }

  public conditionWarnings(): string[] {
    const sets = this.exceptionsEnabled ? [this.mainSet, this.exceptionSet] : [this.mainSet]
    return sets.flatMap((set) => [...this.dateRangeWarnings(set), ...this.numericRangeWarnings(set)])
  }

  private dateRangeWarnings(set: ConditionSet): string[] {
    if (set.matchMode === "any") return []
    const start = set.filterList.find((filter) => filter.infofilter?.value === "StartDateTime" && filter.dateStart && filter.timeSet?.from)
    const end = set.filterList.find((filter) => filter.infofilter?.value === "EndDateTime" && filter.dateEnd && filter.timeSet?.to)
    if (!start || !end) return []
    const startAt = `${start.dateStart}T${start.timeSet.from}`
    const endAt = `${end.dateEnd}T${end.timeSet.to}`
    return endAt <= startAt ? ["The end date & time is before the start date & time, so this element would never be shown."] : []
  }

  private numericRangeWarnings(set: ConditionSet): string[] {
    return set.filterList
      .filter((filter) => this.isNumericFilter(filter))
      .filter((filter) => {
        const min = this.codec.numericBoundValue(filter, 0)
        const max = this.codec.numericBoundValue(filter, 1)
        return min !== null && max !== null && Number(min) > Number(max)
      })
      .map((filter) => `${this.conditionLabel(filter.infofilter)}: the minimum is higher than the maximum, so this condition can never be met.`)
  }

  // ===== Condition catalogue =====
  public get conditionGroups(): ConditionGroup[] {
    return [
      { title: "TIME", conditions: this.conditionsFor(TIME_CONDITION_VALUES) },
      { title: "RESERVATION", conditions: this.conditionsFor(null) },
      { title: "ACCOMMODATION", conditions: this.accommodationConditions() },
    ]
  }

  private conditionsFor(values: string[] | null): LabelValue[] {
    if (values) return this.listenableEvents.filter((event) => values.includes(event.value) && !this.isAccommodationCondition(event))
    return this.listenableEvents.filter((event) => !TIME_CONDITION_VALUES.includes(event.value) && !this.isAccommodationCondition(event))
  }

  private accommodationConditions(): LabelValue[] {
    return this.listenableEvents.filter((event) => this.isAccommodationCondition(event))
  }

  private isAccommodationCondition(condition: LabelValue): boolean {
    return ACCOMMODATION_CONDITION_LABELS.includes(condition.label)
  }

  // every template reference to a condition's name goes through this, so the PMS wording
  // is never shown to the user while `label` stays intact for the save path
  public conditionLabel(condition: LabelValue): string {
    return CONDITION_LABEL_TRANSLATIONS[condition.label] ?? condition.label
  }

  public conditionIcon(condition: LabelValue): string {
    const key = this.iconKeyForField(condition.fieldName) ?? CONDITION_ICON_KEYS[condition.value]
    return this.iconService.Icon[key] ?? this.iconService.Icon.filter_out
  }

  private iconKeyForField(fieldName: string | undefined): IconKey | undefined {
    if (!fieldName) return undefined
    const lastSegment = fieldName.split(":").pop() ?? fieldName
    return CONDITION_ICON_FIELDS[fieldName] ?? CONDITION_ICON_FIELDS[lastSegment]
  }

  // ===== Footer summary =====
  // the banner reads back what was configured, so it only appears once there is something
  // to read back — an empty filter list would render a dangling "SHOW THIS SECTION IF:"
  public get hasConditionSummary(): boolean {
    return !!this.mainSummaryText || !!this.exceptionSummaryText
  }

  public get summaryLeadIn(): string {
    return this.visibilityMode === "Show" ? "SHOW THIS SECTION IF:" : "HIDE THIS SECTION IF:"
  }

  // the consumer renders the saved text as-is, so the mode has to travel with it rather
  // than being hardcoded there
  public get conditionTextLeadIn(): string {
    return this.visibilityMode === "Show" ? "Visible if:" : "Hidden if:"
  }

  public get conditionTextExceptionLeadIn(): string {
    return this.visibilityMode === "Show" ? "but hidden if" : "but visible if"
  }

  public get mainSummaryText(): string {
    return this.conditionTextFor(this.mainSet)
  }

  // the summary renders each condition separately so the joiners between them can be
  // emphasised; the flat text is still what gets saved
  public get mainSummaryParts(): string[] {
    return this.conditionTextParts(this.mainSet)
  }

  // exceptions invert the main mode, so the wording flips with it
  public get exceptionLeadIn(): string {
    return this.visibilityMode === "Show" ? "BUT HIDE IF:" : "BUT SHOW IF:"
  }

  public get exceptionSummaryText(): string {
    if (!this.exceptionsEnabled) return ""
    return this.conditionTextFor(this.exceptionSet)
  }

  public get exceptionSummaryParts(): string[] {
    if (!this.exceptionsEnabled) return []
    return this.conditionTextParts(this.exceptionSet)
  }

  // ===== Summary text =====
  public generateConditionText(): string {
    const main = this.conditionTextFor(this.mainSet)
    if (!main) return ""

    const exceptions = this.exceptionsEnabled && this.exceptionSet.filterList.length > 0 ? this.conditionTextFor(this.exceptionSet) : ""
    const body = exceptions ? `${main}, ${this.conditionTextExceptionLeadIn} ${exceptions}` : main
    return `${this.conditionTextLeadIn} ${body}`
  }

  private conditionTextFor(set: ConditionSet): string {
    return this.conditionTextParts(set).join(` ${this.setJoiner(set)} `)
  }

  public setJoiner(set: ConditionSet): string {
    return set.matchMode === "any" ? "OR" : "AND"
  }

  private conditionTextParts(set: ConditionSet): string[] {
    const parts: string[] = []
    const bounds: { start?: DateTimeBound; end?: DateTimeBound } = {}

    set.filterList.forEach((filter) => {
      if (!filter.infofilter?.label) return

      const field = this.conditionLabel(filter.infofilter)

      if (filter.infofilter.value === "StartDate" || filter.infofilter.value === "EndDate") {
        const direction = filter.comparer?.value === "Before" ? "before" : "after"
        const offset = filter.offSetDays
        if (offset === 0 || offset === undefined) {
          parts.push(`${field} is ${direction} today`)
        } else {
          const absOffset = Math.abs(Number(offset))
          parts.push(`${field} is ${direction} today with offset of ${absOffset} day${absOffset !== 1 ? "s" : ""}`)
        }
      } else if (filter.infofilter.value === RESERVATION_STATUS_VALUE) {
        const comparer = filter.comparer?.label?.toLowerCase() ?? "equals"
        const statuses = filter.reservationStatuses ?? []
        const labels = statuses.map((status) => `"${status.label}"`).join(" or ")
        parts.push(`Reservation Status ${comparer} ${labels}`)
      } else if (this.isNumericFilter(filter)) {
        parts.push(this.numericConditionText(filter, field))
      } else if (filter.infofilter.dynamicVariable) {
        const rule = filter.usedFunction ? this.matchTypeLabel(filter.usedFunction).toLowerCase() : ""
        const values = Array.isArray(filter.svValues) && filter.svValues.length > 1 ? `${filter.svValues.length} value${filter.svValues.length !== 1 ? "s" : ""}` : (filter.svValues[0] ?? "")
        parts.push(`${field} ${rule} "${values}"`.trim())
      } else if (filter.infofilter.value === "StartDateTime") {
        const from = this.formatDisplayDateTime(filter.dateStart, filter.timeSet?.from)
        if (bounds.start === undefined) bounds.start = { value: from, index: parts.length }
        parts.push(`Date & Time is after ${from}`)
      } else if (filter.infofilter.value === "EndDateTime") {
        const to = this.formatDisplayDateTime(filter.dateEnd, filter.timeSet?.to)
        if (bounds.end === undefined) bounds.end = { value: to, index: parts.length }
        parts.push(`Date & Time is before ${to}`)
      } else if (filter.infofilter.value === "WeekTimeSpan") {
        const days = filter.selectedWeekDays?.map((day) => this.dayNameFromLabel(day)).join(", ") ?? ""
        if (filter.selectedWeekDays?.length === 1) {
          if (filter.visibilityDays?.timeConfig?.value === "DifferentHoursSelectedDays") {
            filter.selectedWeekDays.forEach((day) => {
              var timeFrom = filter.visibilityDays[this.dayNameFromLabel(day)].from
              var timeTo = filter.visibilityDays[this.dayNameFromLabel(day)].to
              var timeText = filter.visibilityDays[this.dayNameFromLabel(day)].AllDay ? "" : ` and time is between ${timeFrom} and ${timeTo}`
              parts.push(`Today is ${days}${timeText}`)
            })
          } else {
            var timeFrom = filter.timeSet?.from
            var timeTo = filter.timeSet?.to
            var timeText = filter.visibilityDays?.AllDay ? "" : ` and time is between ${timeFrom} and ${timeTo}`
            parts.push(`Today is ${days}${timeText}`)
          }
        } else {
          if (filter.visibilityDays?.timeConfig?.value === "DifferentHoursSelectedDays") {
            var differentHours = false
            filter.selectedWeekDays.forEach((day) => {
              if (!filter.visibilityDays[this.dayNameFromLabel(day)].AllDay) {
                differentHours = true
              }
            })
            if (differentHours) {
              parts.push(`Today is ${days} within multiple time ranges`)
            } else {
              parts.push(`Today is ${days}`)
            }
          } else {
            if (!filter.visibilityDays?.AllDay) {
              var timeFrom = filter.timeSet?.from
              var timeTo = filter.timeSet?.to
              var timeText = ` and time is between ${timeFrom} and ${timeTo}`
              parts.push(`Today is one of ${days}${timeText}`)
            } else {
              parts.push(`Today is ${days}`)
            }
          }
        }
      }
    })
    return this.mergeDateTimeRange(parts, bounds, set)
  }

  // a start and an end bound are one range to the reader, but they are two separate
  // filters; only AND lets them be restated as a single "between" clause
  private mergeDateTimeRange(parts: string[], bounds: { start?: DateTimeBound; end?: DateTimeBound }, set: ConditionSet): string[] {
    const { start, end } = bounds
    if (!start || !end || this.setJoiner(set) !== "AND") return parts

    const merged = [...parts]
    merged[start.index] = `Date & Time is between ${start.value} and ${end.value}`
    merged.splice(end.index, 1)
    return merged
  }

  private formatDisplayDate(dateStr: string): string {
    if (!dateStr) return ""
    return new Date(dateStr).toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" })
  }

  private formatDisplayDateTime(dateStr: string | undefined, time: string | undefined): string {
    if (!dateStr) return ""
    return `${this.formatDisplayDate(dateStr.split("T")[0])}, ${time || "00:00"}`
  }

  // ===== Drop-down data sources =====
  public reservationStatusItems(filter: filterFunctions): DropDownItem[] {
    const cached = this.statusItemsCache.get(filter)
    if (cached) return cached

    const selected = (filter.reservationStatuses ?? []).map((status) => status.value)
    const items = this.groupedReservationStatuses().map((option) => ({
      ...new DropDownItem(),
      title: option.label,
      checkbox: "checkbox" as const,
      selected: selected.includes(option.value),
    }))
    this.statusItemsCache.set(filter, items)
    return items
  }

  private groupedReservationStatuses(): LabelValue[] {
    return RESERVATION_STATUS_GROUPS.flatMap((group) => group.values.map((value) => this.reservationStatuses.find((status) => status.value === value)).filter((status): status is LabelValue => !!status))
  }

  // the option list is the menu of choices, not the chosen values — every value ever
  // offered stays on it, and svValues is derived purely from which ones are ticked.
  // cached per filter because the drop-down toggles item.selected in place
  //
  // options come from the hotel's saved drop-down values for the field, plus anything the
  // user has typed that the list doesn't offer (yet), so a saved condition keeps its values
  //
  // values the hotel saved as customField sit under the drop-down's "Custom" heading, the same
  // as ones added by hand this session. flow-drop-down derives that heading from the first
  // item with `custom`, treating everything after it as custom, so they all go at the tail.
  public dropdownValueItems(filter: filterFunctions): DropDownItem[] {
    const cached = this.dropdownValueItemsCache.get(filter)
    if (cached) return cached

    const selected = filter.svValues ?? []
    const saved = this.dropDownFieldOptions(filter)
    const known = saved.map((option) => option.value)

    const items = saved.filter((option) => !option.customField).map((option) => this.buildDropdownValueItem(option.value, selected.includes(option.value), false))
    saved.filter((option) => option.customField).forEach((option) => items.push(this.buildDropdownValueItem(option.value, selected.includes(option.value), true)))
    selected.filter((value) => !known.includes(value)).forEach((value) => items.push(this.buildDropdownValueItem(value, true, true)))

    // the field's values may not have arrived yet, and an empty array would be cached for good
    if (known.length || this.hasDropDownField(filter)) this.dropdownValueItemsCache.set(filter, items)
    return items
  }

  // dropDownSupportList is keyed with dots, dynamic variable fieldNames with colons.
  // values deleted this session are dropped here so a second condition on the same field
  // doesn't keep offering one the user just removed
  private dropDownFieldOptions(filter: filterFunctions): DropDownFieldValue[] {
    const field = this.dropDownFieldKey(filter)
    const deleted = this.deletedCustomValues.get(field)
    const options = this.dropDownSupportList[field] ?? []
    return deleted ? options.filter((option) => !deleted.has(option.value)) : options
  }

  private dropDownFieldValues(filter: filterFunctions): string[] {
    return this.dropDownFieldOptions(filter).map((option) => option.value)
  }

  private hasDropDownField(filter: filterFunctions): boolean {
    return this.dropDownFieldKey(filter) in this.dropDownSupportList
  }

  private dropDownFieldKey(filter: filterFunctions): string {
    return (filter.infofilter?.fieldName ?? "").replaceAll(":", ".")
  }

  private buildDropdownValueItem(value: string, selected: boolean, custom: boolean): DropDownItem {
    return { ...new DropDownItem(), title: value, checkbox: "checkbox" as const, custom, selected }
  }

  private commitTickedDropdownValues(filter: filterFunctions, index: number, set: ConditionSet): void {
    const ticked = (this.dropdownValueItemsCache.get(filter) ?? []).filter((option) => option.selected).map((option) => option.title)
    this.withSet(set, () => this.tagsInput(ticked, index))
  }

  // flow-drop-down replaces its items array when the list changes, so the cached array
  // has to track that or the next render would resurrect the old one
  private removeCachedDropdownValue(filter: filterFunctions, item: DropDownItem): void {
    const cached = this.dropdownValueItemsCache.get(filter)
    if (!cached) return
    const position = cached.indexOf(item)
    if (position !== -1) cached.splice(position, 1)
  }

  public timeConfigItems(filter: filterFunctions): DropDownItem[] {
    return this.toDropDownItems(this.timeConfigurationOptions, filter.visibilityDays?.timeConfig?.value)
  }

  private toDropDownItems(options: LabelValue[], selectedValue: string | undefined): DropDownItem[] {
    return options.map((option) => ({
      ...new DropDownItem(),
      title: option.label,
      selected: option.value === selectedValue,
    }))
  }

  // ===== Match type =====
  // segments are the intersection of what the backend supports for this field and what
  // MATCH_TYPE_COPY allows, so every text condition gets the control without per-field code
  public matchTypeSegments(index: number, set: ConditionSet): FlowSegment[] {
    return this.textMatchRules(index, set).map((rule) => ({ label: this.matchTypeLabel(rule), value: rule.functionType }))
  }

  public matchTypeDescription(index: number, set: ConditionSet, condition: LabelValue): string {
    const active = this.activeMatchRule(index, set)
    if (!active) return ""
    const copy = MATCH_TYPE_COPY[active.label?.toLowerCase() ?? ""]
    if (!copy) return ""
    return copy.description.replace("{field}", this.conditionLabel(condition).toLowerCase())
  }

  // a legacy rule has no segment to highlight, so nothing is selected rather than the
  // first allowlisted rule standing in - the control must not claim a match type that
  // isn't what the condition actually evaluates
  public activeMatchType(index: number, set: ConditionSet): string {
    if (this.legacyMatchRuleLabel(index, set)) return ""
    return this.activeMatchRule(index, set)?.functionType ?? ""
  }

  private activeMatchRule(index: number, set: ConditionSet): LabelValue | undefined {
    const used = set.filterList[index]?.usedFunction
    const rules = this.textMatchRules(index, set)
    return rules.find((rule) => rule.functionType === used?.functionType) ?? rules[0]
  }

  // the backend display name of an unsupported comparison this condition was saved with,
  // or "" when the stored rule is one the Match type control can represent
  public legacyMatchRuleLabel(index: number, set: ConditionSet): string {
    return this.legacyRuleLabelFor(set.filterList[index])
  }

  private legacyRuleLabelFor(filter: filterFunctions | undefined): string {
    const used = filter?.usedFunction
    if (!used?.functionType) return ""
    if (!isLegacyMatchRuleName(used.label)) return ""
    return used.label ?? ""
  }

  public hasLegacyMatchRule(index: number, set: ConditionSet): boolean {
    return !!this.legacyMatchRuleLabel(index, set)
  }

  // ===== Legacy status comparison =====
  public hasLegacyStatusComparer(index: number, set: ConditionSet): boolean {
    return !!set.filterList[index]?.legacyStatusComparer
  }

  public legacyStatusSummary(index: number, set: ConditionSet): string {
    return set.filterList[index]?.legacyStatusComparer ?? ""
  }

  // the three outcomes of reading an old ordinal comparison: expanded cleanly, compared
  // against a status outside the stay lifecycle, or a range that never matched anything
  public legacyStatusFixHint(index: number, set: ConditionSet): string {
    const filter = set.filterList[index]
    if (!filter?.legacyStatusComparer) return ""

    if (filter.legacyStatusIssue === "offLifecycle") {
      return "Cancelled and No show aren't stages of a stay, so this comparison has no direct equivalent. Pick the statuses it should apply to."
    }
    if (filter.legacyStatusIssue === "emptyRange") {
      return "No status fell inside that range, so this condition never matched anything. Pick the statuses it should apply to."
    }

    const labels = (filter.reservationStatuses ?? []).map((status) => status.label)
    if (!labels.length) return "Pick the statuses this condition should apply to."
    return `Statuses are now picked individually, so this has been set to ${this.joinLabels(labels)}. Check the selection below and save to update it.`
  }

  private joinLabels(labels: string[]): string {
    if (labels.length === 1) return labels[0]
    return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`
  }

  // what the condition was originally set to, phrased the way the reader would recognise it
  // ("Does not contain BRF") rather than as the backend's rule name
  public legacyRuleSummary(index: number, set: ConditionSet): string {
    const label = this.legacyMatchRuleLabel(index, set)
    if (!label) return ""

    const values = (set.filterList[index]?.svValues ?? []).filter((value) => !!value)
    const phrase = this.legacyRulePhrase(label)
    if (!values.length) return phrase
    return `${phrase} ${values.join(", ")}`
  }

  // the backend names read as list operations; the card talks in match-type terms
  private legacyRulePhrase(label: string): string {
    const name = label.toLowerCase().trim()
    if (name === "list match none") return "Does not exactly match"
    return "Does not contain"
  }

  // flipping Show/Hide inverts the whole rule, so it only reproduces the old behaviour when
  // this is the one condition in it - otherwise the negation has to move into Exceptions
  public isOnlyConditionInRule(index: number, set: ConditionSet): boolean {
    if (set.key !== "main") return false
    if (this.exceptionsEnabled && this.exceptionSet.filterList.length > 0) return false
    return this.mainSet.filterList.length === 1 && index === 0
  }

  public legacyFixHint(index: number, set: ConditionSet, condition: LabelValue): string {
    if (!this.hasLegacyMatchRule(index, set)) return ""
    const field = this.conditionLabel(condition)
    const supported = this.legacyRulePhrase(this.legacyMatchRuleLabel(index, set)) === "Does not contain" ? "Contains" : "Exact match"

    if (this.isOnlyConditionInRule(index, set)) {
      return `Since it's the only condition in this rule, switching ${this.visibilityModeLabel} to ${this.visibilityModeLabel === "Show" ? "Hide" : "Show"} and the match type to ${supported} gives the exact same result.`
    }
    return `To rebuild the same logic, turn on Exceptions and set it to Hide when ${field} ${supported} the same values.`
  }

  // MATCH_TYPE_COPY is the allowlist: the backend returns more comparisons than we want to
  // offer, so only mapped ones survive, ordered by the map rather than by backend order
  private textMatchRules(index: number, set: ConditionSet): LabelValue[] {
    const available = (set.rulesList[index] ?? []).filter((rule) => !!rule.functionType)
    return Object.keys(MATCH_TYPE_COPY)
      .map((name) => available.find((rule) => rule.label?.toLowerCase() === name))
      .filter((rule): rule is LabelValue => !!rule)
  }

  // segments only ever pass allowlisted rules, but the summary text also renders numeric and
  // other comparisons that have no entry, so those keep their backend display name
  private matchTypeLabel(rule: LabelValue): string {
    return MATCH_TYPE_COPY[rule.label?.toLowerCase() ?? ""]?.label ?? rule.label ?? ""
  }

  // ===== Dialog actions =====
  // the drop-down writes have to finish before the dialog closes, or ngOnDestroy's unsubscribe
  // cancels them. A failure there doesn't block the condition itself from saving.
  public saveFunction(): void {
    const result = { function: this.functionOutput, text: this.generateConditionText(), visibilityMode: this.visibilityMode }
    const newFields = this.newDropDownFields()
    const removedFields = this.removedDropDownFields()

    const requests = []
    if (newFields.length) requests.push(this.communicationService.Communication.PutDropDownFields(newFields))
    if (removedFields.length) requests.push(this.communicationService.Communication.DeleteDropDownFields(removedFields))
    if (!requests.length) {
      this.dialogRef.close(result)
      return
    }

    forkJoin(requests)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: () => this.dialogRef.close(result),
        error: () => this.dialogRef.close(result),
      })
  }

  // only values the user added and still has selected. Conditions without a fieldName have no
  // drop-down field to save into (the plain phrase input), so they contribute nothing.
  private newDropDownFields(): DropDownFieldValue[] {
    const hotelId = this.stateService.selectedHotel.Id
    return [this.mainSet, this.exceptionSet].flatMap((set) =>
      set.filterList.flatMap((filter) => {
        const field = this.dropDownFieldKey(filter)
        if (!field) return []
        return [...(this.customTags.get(filter) ?? [])].filter((tag) => filter.svValues?.includes(tag)).map((value): DropDownFieldValue => ({ hotelId, field, value, isVisible: true, lastSeen: null, customField: true }))
      }),
    )
  }

  // deletions are collected per field, so this is flat rather than walked per condition
  private removedDropDownFields(): DropDownFieldValue[] {
    const hotelId = this.stateService.selectedHotel.Id
    return [...this.deletedCustomValues.entries()].flatMap(([field, values]) => [...values].map((value): DropDownFieldValue => ({ hotelId, field, value, isVisible: true, lastSeen: null, customField: true })))
  }

  public closeDialog() {
    this.dialogRef.close()
  }

  public setVisibilityMode(mode: string): void {
    this.visibilityMode = mode as VisibilityMode
    this.updateFilterOutput()
  }

  public setMatchMode(mode: string, set: ConditionSet = this.mainSet): void {
    set.matchMode = mode as MatchMode
    this.updateFilterOutput()
  }

  public toggleExceptions(enabled: boolean): void {
    this.exceptionsEnabled = enabled
    if (!enabled) {
      this.exceptionSet.filterList = []
      this.exceptionSet.rulesList = []
      this.exceptionSet.collapsedGroups.clear()
    }
    this.updateFilterOutput()
  }

  // ===== Template event handlers =====
  // expanding a card adds its filter so the controls have somewhere to write; collapsing one
  // that was never filled in drops it again, so an idly opened card cannot block Save
  public onConditionExpanded(condition: LabelValue, expanded: boolean, set: ConditionSet): void {
    const index = this.filterIndexFor(condition, set)
    if (!expanded) {
      if (index !== -1 && !this.isFilterValid(set.filterList[index])) this.removeFilterAt(index, set)
      return
    }
    if (index !== -1) return
    this.withSet(set, () => this.addChosenConditional({ ...condition }, set.filterList.length))
  }

  // Clear resets the card to the same blank state a freshly expanded one has, so the
  // controls stay usable; collapsing it afterwards drops the filter from the list
  public onClearCondition(condition: LabelValue, set: ConditionSet): void {
    const index = this.filterIndexFor(condition, set)
    if (index === -1) return
    this.withSet(set, () => this.addChosenConditional({ ...condition }, index))
    this.updateFilterOutput()
  }

  // rulesList is indexed in lockstep with filterList, so both are spliced together
  private removeFilterAt(index: number, set: ConditionSet): void {
    set.filterList.splice(index, 1)
    set.rulesList.splice(index, 1)
    this.updateFilterOutput()
  }

  public onSetReservationStatus(items: DropDownItem[], index: number, set: ConditionSet): void {
    const titles = items.map((item) => item.title)
    const options = this.groupedReservationStatuses().filter((option) => titles.includes(option.label))
    this.withSet(set, () => this.setReservationStatus(options, index))
  }

  public onRemoveReservationStatus(status: LabelValue, index: number, set: ConditionSet): void {
    const filter = set.filterList[index]
    if (!filter) return

    const remaining = (filter.reservationStatuses ?? []).filter((current) => current.value !== status.value)
    // the drop-down's checkbox state lives on the cached items, so untick it there too
    const item = this.statusItemsCache.get(filter)?.find((cached) => cached.title === status.label)
    if (item) item.selected = false

    this.withSet(set, () => this.setReservationStatus(remaining, index))
  }

  public onSetDropdownValues(items: DropDownItem[], index: number, set: ConditionSet): void {
    const values = items.map((item) => item.title).filter((value, position, all) => all.indexOf(value) === position)
    this.withSet(set, () => this.tagsInput(values, index))
  }

  public onDropdownValueAdded(item: DropDownItem, filter: filterFunctions, index: number, set: ConditionSet): void {
    item.selected = true
    const cached = this.dropdownValueItemsCache.get(filter) ?? this.seedDropdownValueCache(filter)
    if (!cached.includes(item)) cached.push(item)
    this.rememberCustomTag(filter, item.title)
    this.commitTickedDropdownValues(filter, index, set)
  }

  private seedDropdownValueCache(filter: filterFunctions): DropDownItem[] {
    const items = this.dropdownValueItems(filter)
    this.dropdownValueItemsCache.set(filter, items)
    return items
  }

  public onDropdownValueRemoved(item: DropDownItem, filter: filterFunctions, index: number, set: ConditionSet): void {
    this.removeCachedDropdownValue(filter, item)
    const wasPending = this.customTags.get(filter)?.delete(item.title) ?? false
    if (!wasPending && this.isSavedCustomValue(filter, item.title)) this.markCustomValueDeleted(filter, item.title)
    this.commitTickedDropdownValues(filter, index, set)
  }

  private isSavedCustomValue(filter: filterFunctions, value: string): boolean {
    return this.dropDownFieldOptions(filter).some((option) => option.value === value && option.customField)
  }

  private markCustomValueDeleted(filter: filterFunctions, value: string): void {
    const field = this.dropDownFieldKey(filter)
    if (!field) return
    const values = this.deletedCustomValues.get(field) ?? new Set<string>()
    values.add(value)
    this.deletedCustomValues.set(field, values)
    this.dropDeletedValueFromOtherConditions(field, value, filter)
  }

  private dropDeletedValueFromOtherConditions(field: string, value: string, source: filterFunctions): void {
    ;[this.mainSet, this.exceptionSet].forEach((set) =>
      set.filterList.forEach((filter, index) => {
        if (filter === source || this.dropDownFieldKey(filter) !== field) return
        const cached = this.dropdownValueItemsCache.get(filter)
        const item = cached?.find((option) => option.title === value)
        if (item) this.removeCachedDropdownValue(filter, item)
        if (filter.svValues?.includes(value))
          this.withSet(set, () =>
            this.tagsInput(
              filter.svValues.filter((current) => current !== value),
              index,
            ),
          )
      }),
    )
  }

  public onMatchTypeChange(functionType: string, index: number, set: ConditionSet): void {
    const rule = this.textMatchRules(index, set).find((option) => option.functionType === functionType)
    if (!rule) return
    const values = [...(set.filterList[index]?.svValues ?? [])]
    this.withSet(set, () => {
      this.setRule(rule, index)
      this.filterList[index].svValues = values
      this.updateFilterOutput()
    })
  }

  public onTagDraftInput(value: string, filter: filterFunctions): void {
    this.tagDrafts.set(filter, value ?? "")
  }

  public onAddTag(filter: filterFunctions, index: number, set: ConditionSet): void {
    const tag = this.tagDraft(filter).trim()
    if (!tag) return

    const current = filter.svValues ?? []
    if (current.includes(tag)) return

    this.tagDrafts.set(filter, "")
    this.rememberCustomTag(filter, tag)
    this.withSet(set, () => this.tagsInput([...current, tag], index))
  }

  private rememberCustomTag(filter: filterFunctions, tag: string): void {
    if (this.dropDownFieldValues(filter).includes(tag)) return
    const tags = this.customTags.get(filter) ?? new Set<string>()
    tags.add(tag)
    this.customTags.set(filter, tags)
  }

  public onRemoveTag(tag: string, index: number, set: ConditionSet): void {
    const filter = set.filterList[index]
    if (!filter) return

    // the drop-down's checkbox state lives on the cached items, so untick it there too.
    // the option stays on the list, just unticked, so it can be picked again
    const item = this.dropdownValueItemsCache.get(filter)?.find((cached) => cached.title === tag)
    if (item) item.selected = false

    this.customTags.get(filter)?.delete(tag)
    const remaining = (filter.svValues ?? []).filter((value) => value !== tag)
    this.withSet(set, () => this.tagsInput(remaining, index))
  }

  public onMinInput(event: any, index: number, set: ConditionSet): void {
    this.withSet(set, () => this.rangeInput(event, index, 0))
  }

  public onMaxInput(event: any, index: number, set: ConditionSet): void {
    this.withSet(set, () => this.rangeInput(event, index, 1))
  }

  public onChangeOffset(event: any, index: number, set: ConditionSet): void {
    this.withSet(set, () => this.changeOffset(event, index))
  }

  public onBeforeAfterOffset(direction: string, index: number, set: ConditionSet): void {
    this.withSet(set, () => this.beforeAfterOffset(direction, index))
  }

  public onSetDate(event: Date[], index: number, type: "dateStart" | "dateEnd", set: ConditionSet): void {
    this.withSet(set, () => this.setDate(event, index, type))
  }

  public onSelectDay(day: string, index: number, set: ConditionSet): void {
    this.withSet(set, () => this.selectDay(day, index))
  }

  public onTimeConfigChange(items: DropDownItem[], index: number, set: ConditionSet): void {
    const option = this.timeConfigurationOptions.find((o) => o.label === items[0]?.title)
    if (!option) return
    this.withSet(set, () => this.timeConfigChange(option, index))
  }

  public onAllDayChange(event: any, index: number, day: string, set: ConditionSet): void {
    this.withSet(set, () => this.allDayChange(event, index, day))
  }

  public onTimeInputChange(event: Event, type: "from" | "to", index: number, day: string, set: ConditionSet): void {
    this.withSet(set, () => this.onTimeChange(event, type, index, day))
  }

  // ===== Template helpers =====
  public toggleGroup(title: string, set: ConditionSet): void {
    set.collapsedGroups.has(title) ? set.collapsedGroups.delete(title) : set.collapsedGroups.add(title)
  }

  public isGroupCollapsed(title: string, set: ConditionSet): boolean {
    return set.collapsedGroups.has(title)
  }

  public groupSummary(group: ConditionGroup, set: ConditionSet): string {
    return `${group.conditions.filter((condition) => this.isConditionSet(condition, set)).length} of ${group.conditions.length} set`
  }

  public isConditionSet(condition: LabelValue, set: ConditionSet): boolean {
    const filter = set.filterList[this.filterIndexFor(condition, set)]
    return !!filter && this.isFilterValid(filter)
  }

  // a condition carrying an old rule is filled in, it just can't be saved as-is, so it
  // reads as needing attention rather than as never having been set
  public conditionDisabledText(condition: LabelValue, set: ConditionSet): string {
    if (this.conditionHasLegacyRule(condition, set)) return ""
    return this.isConditionSet(condition, set) ? "" : "Not set"
  }

  public conditionBadge(condition: LabelValue, set: ConditionSet): FlowCardBadge | null {
    if (this.conditionHasLegacyRule(condition, set)) return { text: "Review conditions", color: "Warning1", type: "Subtle", icon: this.iconService.Icon.exclamation_mark_1_5 }
    return this.isConditionSet(condition, set) ? { text: "Conditions set", color: "Info1", type: "Subtle" } : null
  }

  // either kind of outdated setup: an unsupported text comparison, or a status condition
  // whose ordinal comparer was expanded into explicit statuses on load
  public conditionHasLegacyRule(condition: LabelValue, set: ConditionSet): boolean {
    const filter = set.filterList[this.filterIndexFor(condition, set)]
    return !!this.legacyRuleLabelFor(filter) || !!filter?.legacyStatusComparer
  }

  public filterIndexFor(condition: LabelValue, set: ConditionSet = this.mainSet): number {
    return set.filterList.findIndex((filter) => filter.infofilter?.value === condition.value && filter.infofilter?.fieldName === condition.fieldName)
  }

  public get visibilityModeLabel(): string {
    return this.visibilityMode === "Hide" ? "Hide" : "Show"
  }

  public tagDraft(filter: filterFunctions): string {
    return this.tagDrafts.get(filter) ?? ""
  }

  public dayNameFromLabel(label: string): WeekDayName {
    return this.weekDayLabels[label]
  }

  public toDate(dateStr: string): Date | null {
    return dateStr ? new Date(dateStr) : null
  }

  public offsetMarginLeft(filter: filterFunctions): string {
    const offset = Number(filter.offSetDays ?? 0)
    return offset > 0 && offset <= OFFSET_LIMIT ? `${offset * OFFSET_STEP_PX}px` : "0px"
  }

  public offsetMarginRight(filter: filterFunctions): string {
    const offset = Number(filter.offSetDays ?? 0)
    return offset < 0 && offset >= -OFFSET_LIMIT ? `${Math.abs(offset) * OFFSET_STEP_PX}px` : "0px"
  }

  public offsetSliderId(index: number, set: ConditionSet): string {
    return `offSetSlider-${set.key}-${index}`
  }

  public radioGroupName(index: number, set: ConditionSet): string {
    return `radio-${set.key}-${index}`
  }
}
